import React, { useState, useEffect } from 'react';
import {
    Table,
    Button,
    Space,
    Modal,
    message,
    Typography,
    Card,
    Tag,
    Switch,
} from 'antd';
import { CheckOutlined, CloseOutlined, EyeOutlined } from '@ant-design/icons';
import axios from 'axios';

const { Title } = Typography;

const PartnerRequests = () => {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });

    // Fetch partner requests
    const fetchRequests = async (page = 1, pageSize = 10, search = '') => {
        setLoading(true);
        try {
            const response = await axios.get('/admins/partners', {
                params: {
                    page,
                    perPage: pageSize,
                    search,
                }
            });
            setRequests(response.data.partners || []);
            setPagination(prev => ({
                ...prev,
                total: response.data.totalPartners || 0,
            }));
        } catch (error) {
            message.error('Failed to fetch partner requests');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRequests();
    }, []);

    const handleApprovalChange = async (id, isApproved) => {
        try {
            await axios.patch(`/admins/partners/${id}/${isApproved ? 'approve' : 'reject'}`);
            message.success(`Partner ${isApproved ? 'approved' : 'rejected'} successfully`);
            fetchRequests(pagination.current, pagination.pageSize);
        } catch (error) {
            message.error('Failed to update partner status');
        }
    };

    const showRequestDetails = (request) => {
        setSelectedRequest(request);
        setIsModalVisible(true);
    };

    const handleTableChange = (pagination, filters, sorter) => {
        fetchRequests(pagination.current, pagination.pageSize);
    };

    const columns = [
        {
            title: 'Company Name',
            dataIndex: 'companyName',
            key: 'companyName',
        },
        {
            title: 'Company Type',
            dataIndex: 'companyType',
            key: 'companyType',
        },
        {
            title: 'Contact Name',
            key: 'contactName',
            render: (_, record) => `${record.name} ${record.surname}`,
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
        },
        {
            title: 'EIN',
            dataIndex: 'ein',
            key: 'ein',
        },
        {
            title: 'Status',
            key: 'status',
            render: (_, record) => (
                <Space>
                    <Tag color={record.isApproved ? 'green' : 'red'}>
                        {record.isApproved ? 'APPROVED' : 'PENDING'}
                    </Tag>
                    <Switch
                        checked={record.isApproved}
                        onChange={(checked) => handleApprovalChange(record._id, checked)}
                    />
                </Space>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button
                        icon={<EyeOutlined />}
                        onClick={() => showRequestDetails(record)}
                    >
                        View
                    </Button>
                </Space>
            ),
        },
    ];

    return (
        <div style={{ padding: '24px' }}>
            <Card>
                <Title level={2}>Partner Management</Title>
                <Table
                    columns={columns}
                    dataSource={requests}
                    loading={loading}
                    rowKey="_id"
                    pagination={pagination}
                    onChange={handleTableChange}
                />
            </Card>

            <Modal
                title="Partner Details"
                open={isModalVisible}
                onCancel={() => setIsModalVisible(false)}
                footer={null}
                width={800}
            >
                {selectedRequest && (
                    <div>
                        <p><strong>Company Name:</strong> {selectedRequest.companyName}</p>
                        <p><strong>Company Type:</strong> {selectedRequest.companyType}</p>
                        <p><strong>Contact Name:</strong> {selectedRequest.name} {selectedRequest.surname}</p>
                        <p><strong>Email:</strong> {selectedRequest.email}</p>
                        <p><strong>EIN:</strong> {selectedRequest.ein}</p>
                        <p><strong>Email Notifications:</strong> {selectedRequest.emailNotifications ? 'Enabled' : 'Disabled'}</p>
                        <p><strong>Status:</strong> {selectedRequest.isApproved ? 'Approved' : 'Pending'}</p>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default PartnerRequests;
